import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { ModalTemplateWrapper } from '../ModalTemplateWrapper/ModalTemplateWrapper.js';
import { type ModalTemplateWrapperProps } from '../ModalTemplateWrapper/types.js';
import { type CommonTemplateProps } from '../types.js';
import { AlertCircleIcon } from '@contra/icons/icons';
import { useCallback } from 'react';

type DisableWorkWithMeConfirmationModalProps = CommonTemplateProps & {
  readonly onToggleWorkWithMe: () => void;
  readonly targetPersona: string;
};

export const DisableWorkWithMeConfirmationModal = ({
  closeModal,
  isOpen,
  modalName,
  onToggleWorkWithMe,
  targetPersona,
}: DisableWorkWithMeConfirmationModalProps) => {
  const { track } = useAnalytics();

  const handleOnConfirm = useCallback(() => {
    onToggleWorkWithMe();
    closeModal();

    track('modal', {
      action: 'tapped',
      cta: 'primary',
      modal_name: modalName,
    });
  }, [closeModal, modalName, onToggleWorkWithMe, track]);

  const handleOnCancel = useCallback(() => {
    closeModal();

    track('modal', {
      action: 'tapped',
      cta: 'secondary',
      modal_name: modalName,
    });
  }, [closeModal, modalName, track]);

  const modalConfiguration: Partial<ModalTemplateWrapperProps> = {
    ctaOptions: {
      primaryCTA: {
        handleOnClick: handleOnConfirm,
        label: 'Turn Off',
      },
      secondaryCTA: {
        handleOnClick: handleOnCancel,
        label: 'Never mind',
      },
    },
    headerText: 'Are you sure?',
    icon: <AlertCircleIcon />,
    subheaderText: `By disabling your "Get In Touch" button, ${targetPersona}s won't be able to contact you, and you will no longer appear in Discover.`,
  };

  return (
    <ModalTemplateWrapper
      ariaLabel="Disable Work With Me Confirmation Modal"
      desktopModalSize="md"
      isOpen={isOpen}
      onClose={closeModal}
      {...modalConfiguration}
    />
  );
};
